<script lang="ts">
    import {getLocalization} from 'src/core';
    import {MarkdownRichTextAreaToolbar} from 'shared/ui-widgets/markdown-richtext-area/toolbar';
    import {type MarkdownRichTextAreaOptions, defaultMarkdownRichTextAreaOptions} from './types';
    import {createRichtextAreaContext} from './richtext-area.context';
    import {createEventDispatcher} from 'svelte';
    import {createPlaceholderPlugin, createBetterBackspacePlugin} from './plugins';
    import {Editor, editorViewCtx, defaultValueCtx, editorViewOptionsCtx, rootCtx} from '@milkdown/core';
    import {get} from 'svelte/store';
    import {commonmark} from '@milkdown/preset-commonmark';
    import {gfm} from '@milkdown/preset-gfm';
    import {history} from '@milkdown/plugin-history';
    import {listener, listenerCtx} from '@milkdown/plugin-listener';
    import {clipboard} from '@milkdown/plugin-clipboard';
    import {cursor} from '@milkdown/plugin-cursor';
    import {prosePluginsCtx} from '@milkdown/core';

    export let options: MarkdownRichTextAreaOptions = defaultMarkdownRichTextAreaOptions;
    export let value = '';
    export let disabled = false;

    const dispatch = createEventDispatcher<{ 'markdown-updated': string }>();
    const localize = getLocalization();
    const {setEditor, editor$} = createRichtextAreaContext();

    $: toggleDisabledState(disabled);

    function editorAction(node: Node) {
        // Init our custom plugins
        const betterBackspacePluginInstance = createBetterBackspacePlugin();
        const placeholderPluginInstance = createPlaceholderPlugin({
            textPlaceholder: localize(/* @kp-localization richTextArea.placeholders.default */ 'richTextArea.placeholders.default'),
            headingPlaceholder: localize(/* @kp-localization richTextArea.placeholders.heading */ 'richTextArea.placeholders.heading'),
            quotePlaceholder: localize(/* @kp-localization richTextArea.placeholders.quote */ 'richTextArea.placeholders.quote'),
            codeBlockPlaceholder: localize(/* @kp-localization richTextArea.placeholders.code */ 'richTextArea.placeholders.code')
        });

        // Init editor
        const editor = Editor.make()
            .config((ctx) => {
                ctx.set(rootCtx, node);

                ctx.set(defaultValueCtx, value ?? '');

                ctx.get(listenerCtx).markdownUpdated((_ctx, markdown) => {
                    dispatch('markdown-updated', markdown)
                });

                ctx.update(prosePluginsCtx, (plugins) => {
                    return plugins.concat(placeholderPluginInstance, betterBackspacePluginInstance);
                });
            })
            .use(commonmark)
            .use(gfm)
            .use(history)
            .use(clipboard)
            .use(cursor)
            .use(listener);

        editor.create().then((instance) => {
            setEditor(instance);
        });

        return {
            destroy() {
                editor.destroy();
            },
        };
    }

    function toggleDisabledState(newDisabledState: boolean) {
        const editor = get(editor$);
        if (!editor) {
            return;
        }

        editor.action((ctx) => {
            ctx.update(editorViewOptionsCtx, (prev) => ({
                ...prev,
                editable: () => !newDisabledState,
            }));
        });
    }

    export function focus() {
        const editor = get(editor$);
        if (!editor) {
            return;
        }

        editor.action((ctx) => ctx.get(editorViewCtx).focus());
    }
</script>

<div class="richtext-area" class:popover-mode={options.inPopoverMode}>
    <div class="editor-container">
        <MarkdownRichTextAreaToolbar/>
        <div class="milkdown-container" use:editorAction></div>
    </div>
</div>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";
    @import (reference) "./markdown.styles.less";

    .richtext-area {
        display: flex;
        flex-direction: column;
        border: 1px solid @themed-border-default;
        border-radius: @border-radius-large;
        width: 100%;
        height: 100%;
        font-size: @font-size-default;

        &.popover-mode {
            border: none;
            border-radius: 0;

            .editor-container .milkdown-container {
                min-height: 200px;
            }
        }

        .editor-container {
            display: flex;
            flex-direction: column;
            flex: 1;

            .milkdown-container {
                flex: 1;
                position: relative;
                padding: @spacing-l;
                border-bottom-left-radius: @border-radius-large;
                border-bottom-right-radius: @border-radius-large;

                :global {
                    .milkdown {
                        height: 100%;

                        .ProseMirror.editor {
                            height: 100%;

                            &:focus,
                            &:active,
                            &:focus-visible {
                                outline: none !important;
                                box-shadow: none !important;
                                outline-offset: 0 !important;
                            }

                            .markdown-styles();
                        }
                    }

                    .milkdown-placeholder {
                        pointer-events: none;
                        opacity: 0.4;
                        position: absolute;

                        &.placeholder-bold {
                            font-weight: bold;
                        }

                        &.placeholder-italic {
                            font-style: italic;
                        }

                        &.placeholder-strike {
                            text-decoration: line-through;
                        }

                        &.placeholder-underline {
                            text-decoration: underline;
                        }
                    }
                }
            }
        }
    }
</style>